<template>
  <v-container>
    <v-row>
      <v-col cols="12" md="10" offset-md="1" lg="8" offset-lg="2">
        <v-toolbar>
          <v-toolbar-title>Section Editor</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn color="success" @click="addDialogActive = true">
            <v-icon left>fal fa-plus</v-icon>
            Add Section
          </v-btn>
        </v-toolbar>
        <v-expansion-panels v-model="activeSection">
          <section-panel
            v-for="(section, index) in sections"
            :key="'section-' + index"
            :section="section"
            :index="index"
            :sections="sections"
            @move="({ index, dir }) => move(index, dir)"></section-panel>
        </v-expansion-panels>
        <v-dialog v-if="isAdmin" v-model="addDialogActive" width="500">
          <template v-slot:activator="{ on }">
            <v-btn v-on="on" color="success">
              <v-icon left>fal fa-plus</v-icon>
              Add Section
            </v-btn>
          </template>
          <v-card>
            <v-card-title>Add New Section</v-card-title>
            <v-card-text>
              <v-form lazy-validation ref="newSectionForm">
                <v-text-field
                  v-model="newLabel"
                  :rules="[v => !!v || 'Required', v => (v.toLowerCase() !== 'admin' && v.toLowerCase() !== 'action') || 'Invalid Section Label']"
                  label="Section Label"></v-text-field>
                <v-text-field
                  v-model="newCode"
                  :hint="'Link: orientation.covenant.edu/' + newCode"
                  :rules="[v => !!v || 'Required', v => v.length < 20 || 'Limited to 20 characters', v => (v.toLowerCase() !== 'admin' && v.toLowerCase() !== 'action') || 'Invalid code']"
                  label="Short Code (used for web links)"
                  persistent-hint></v-text-field>
                <p style="margin-top:16px">Will be added in the {{ selectedTermDesc }} term.</p>
              </v-form>
            </v-card-text>
            <v-card-actions>
              <v-btn text @click="addDialogActive = false">Cancel</v-btn>
              <v-btn text color="success" @click="addSection">Create Section</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-col>
    </v-row>
  </v-container>
</template>
<script>
import { hasRole } from '@/helpers/has'
import { ref, computed, watch, onMounted, onBeforeUnmount } from '@vue/composition-api'
export default {
  components: {
    SectionPanel: () => import('@/components/admin/sectionPanel')
  },
  setup (props, { root }) {
    const user = computed(() => root.$store.state.user)
    const service = root.$feathers.service('orientation/section')

    const selectedTermCode = computed(() => root.$store.state.admin.term)
    const selectedTermDesc = computed(() => root.$store.state.admin.termLabel)
    const sections = ref([])

    const onPatchedListener = (data) => {
      for (let i = 0; i < sections.value.length; i++) {
        if (sections.value[i]._id === data._id) {
          let temp = Object.assign({}, sections.value[i], data)
          sections.value.splice(i, 1, temp)
        }
      }
    }
    onMounted(() => {
      if (!hasRole(user.value, 'Employee')) {
        root.$store.dispatch('snackbar', { active: true, color: 'error', timeout: 6000, text: 'You do not have access to the admin system.' })
        root.$router.push('/')
        return
      }
      root.$feathers.service('orientation/section').on('patched', onPatchedListener)
      if (selectedTermCode.value !== '') {
        loadSections()
      }
    })
    onBeforeUnmount(() => { root.$feathers.service('orientation/section').removeListener('patched', onPatchedListener) })

    watch(selectedTermCode, () => {
      loadSections()
    })

    function loadSections () {
      service.find({ query: { term: selectedTermCode.value, $limit: 30, $sort: { order: 1 } } }).then(({ data }) => {
        sections.value = data
      })
    }

    const activeSection = ref(null)

    function move (index, dir) {
      service.patch(sections.value[index]._id, { order: sections.value[index].order + dir })
      service.patch(sections.value[index + dir]._id, { order: sections.value[index].order + (dir * -1) })
      let temp = sections.value.splice(index, 1)
      sections.value.splice(index + dir, 0, temp[0])
      activeSection.value += dir
      root.$store.dispatch('snackbar', { active: true, color: 'success', timeout: 6000, text: 'Section has been moved and saved.' })
    }

    const addDialogActive = ref(false)
    const newLabel = ref('')
    const newCode = ref('')
    const newSectionForm = ref(null)

    function addSection () {
      if (newSectionForm.value.validate()) {
        service.find({ query: { label: { $regex: newLabel.value, $options: 'i' }, code: { $regex: newCode.value, $options: 'i' }, term: selectedTermCode.value } }).then(({ total }) => {
          if (total > 0) {
            alert('Invalid label or code; at least one is already in use by another section')
          } else {
            service.find({ query: { $sort: { order: -1 }, $limit: 1 } }).then(({ data }) => {
              let obj = {
                code: newCode.value,
                label: newLabel.value,
                term: selectedTermCode.value,
                active: false,
                order: 1,
                slides: [
                  {
                    primary: [],
                    secondary: []
                  }
                ]
              }
              if (data.length > 0) {
                obj.order = data[0].order + 1
              }
              service.create(obj).then((section) => {
                let obj = {
                  section: section._id,
                  slideNum: 1,
                  active: true,
                  type: 'TextCard',
                  content: '<h1>' + newLabel.value + '</h1><p>Replace this placeholder content with your own text and add the additional content and slides that you need.</p>'
                }
                root.$feathers.service('orientation/block').create(obj).then((block) => {
                  service.patch(section._id, { 'slides.0.primary': { type: 'TextCard', id: block._id } }).then(loadSections)
                  addDialogActive.value = false
                })
              })
              newCode.value = ''
              newLabel.value = ''
            })
          }
        })
      }
    }

    const isAdmin = computed(() => root.$store.getters.isAdmin)

    return {
      user,
      selectedTermCode,
      selectedTermDesc,
      sections,
      loadSections,
      activeSection,
      move,
      addDialogActive,
      newLabel,
      newCode,
      newSectionForm,
      addSection,
      isAdmin,
      hasRole
    }
  }
}
</script>
